/**
 * rc135 — "Notificaciones en este dispositivo": activar/probar/desactivar Web Push
 * para el usuario actual en ESTE navegador, con la guía de instalación en iPhone.
 * Además lista los otros navegadores/teléfonos donde el usuario ya las activó.
 */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Bell, BellOff, BellRing, Smartphone, Share, Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { apiCall } from "@/lib/api";
import { activarPush, bajaPush, esIOS, instalada, permiso, pushActivo, pushSoportado } from "@/lib/push";
import { formatDateTime } from "@/lib/utils";

type Dispositivo = { id: string; user_agent: string | null; created_at: string; last_ok_at: string | null; last_error: string | null };

function nombreDispositivo(ua: string | null): string {
  if (!ua) return "Navegador";
  if (/iPhone/.test(ua)) return "iPhone";
  if (/iPad/.test(ua)) return "iPad";
  if (/Android/.test(ua)) return "Android";
  if (/Macintosh/.test(ua)) return "Mac";
  if (/Windows/.test(ua)) return "Windows";
  return "Navegador";
}

export function PushDispositivoCard() {
  const qc = useQueryClient();
  const [activo, setActivo] = useState<boolean | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const soportado = pushSoportado();
  const perm = permiso();
  const iosSinInstalar = esIOS() && !instalada();

  useEffect(() => { pushActivo().then(setActivo).catch(() => setActivo(false)); }, []);

  const dispositivos = useQuery<Dispositivo[]>({
    queryKey: ["push", "mis-dispositivos"],
    queryFn: () => apiCall<Dispositivo[]>("get", "/api/v1/notifications/push/mis-dispositivos"),
    staleTime: 30_000,
  });
  const probarMut = useMutation({
    mutationFn: () => apiCall<{ stats: { sent: number; failed: number } }>("post", "/api/v1/notifications/test-push"),
    onSuccess: (r) => {
      if (r.stats.sent > 0) toast.success("Enviada — debe aparecer en unos segundos");
      else toast.warning("No se envió a ningún dispositivo", { description: "Activa las notificaciones aquí primero." });
      qc.invalidateQueries({ queryKey: ["push", "mis-dispositivos"] });
    },
    onError: () => toast.error("No se pudo enviar la prueba"),
  });
  const revocarMut = useMutation({
    mutationFn: (id: string) => apiCall("delete", `/api/v1/notifications/push/mis-dispositivos/${id}`),
    onSuccess: () => { toast.success("Desactivadas en ese dispositivo"); qc.invalidateQueries({ queryKey: ["push", "mis-dispositivos"] }); },
  });

  async function activar() {
    setTrabajando(true);
    try {
      const r = await activarPush();
      if (r === "ok") { setActivo(true); toast.success("Notificaciones activadas en este dispositivo"); qc.invalidateQueries({ queryKey: ["push", "mis-dispositivos"] }); }
      else toast.warning(r);
    } catch (e) {
      toast.error("No se pudieron activar", { description: (e as Error).message });
    } finally { setTrabajando(false); }
  }
  async function desactivar() {
    setTrabajando(true);
    try { await bajaPush(); setActivo(false); toast.success("Notificaciones desactivadas en este dispositivo"); qc.invalidateQueries({ queryKey: ["push", "mis-dispositivos"] }); }
    finally { setTrabajando(false); }
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {activo ? <BellRing size={16} className="text-green-500" /> : <Bell size={16} className="text-muted-foreground" />}
          <div>
            <div className="text-sm font-semibold">Notificaciones en este dispositivo</div>
            <div className="text-[11px] text-muted-foreground">
              {!soportado
                ? iosSinInstalar ? "En iPhone primero instala ByteISP en la pantalla de inicio." : "Este navegador no soporta notificaciones."
                : activo ? "Activas: recibirás tickets, comentarios y pagos aquí."
                : perm === "denied" ? "Permiso bloqueado en el navegador — habilítalo en sus ajustes del sitio."
                : "Apagadas en este navegador."}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {activo ? (
            <>
              <Button size="sm" variant="outline" onClick={() => probarMut.mutate()} disabled={probarMut.isPending}>
                {probarMut.isPending ? <Loader2 size={12} className="animate-spin" /> : <BellRing size={12} />} Probar
              </Button>
              <Button size="sm" variant="outline" onClick={desactivar} disabled={trabajando}>
                <BellOff size={12} /> Desactivar
              </Button>
            </>
          ) : (
            <Button size="sm" onClick={activar} disabled={trabajando || !soportado || perm === "denied"}>
              {trabajando ? <Loader2 size={12} className="animate-spin" /> : <Bell size={12} />} Activar
            </Button>
          )}
        </div>
      </div>

      {iosSinInstalar && (
        <div className="rounded border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
          <div className="mb-1 flex items-center gap-1.5 font-medium text-foreground"><Smartphone size={13} /> Instalar en iPhone (Safari)</div>
          Toca <Share size={12} className="inline align-middle" /> <b>Compartir</b> → <b>Agregar a pantalla de inicio</b> → abre ByteISP desde el icono y vuelve aquí para activar.
          Las notificaciones en iPhone solo funcionan desde la app instalada (iOS 16.4+).
        </div>
      )}

      {dispositivos.data && dispositivos.data.length > 0 && (
        <div className="space-y-1">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Dispositivos con notificaciones activas</div>
          {dispositivos.data.map((d) => (
            <div key={d.id} className="flex items-center justify-between gap-2 rounded border px-2 py-1.5 text-xs">
              <div className="min-w-0">
                <span className="font-medium">{nombreDispositivo(d.user_agent)}</span>
                <span className="ml-2 text-muted-foreground">desde {formatDateTime(d.created_at)}</span>
                {d.last_error && <span className="ml-2 text-amber-600">· último envío falló</span>}
                {!d.last_error && d.last_ok_at && <span className="ml-2 text-muted-foreground">· último {formatDateTime(d.last_ok_at)}</span>}
              </div>
              <Button size="sm" variant="ghost" onClick={() => revocarMut.mutate(d.id)} disabled={revocarMut.isPending} title="Desactivar en ese dispositivo">
                <Trash2 size={12} />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
